import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import SearchableSelect from '../common/SearchableSelect';

const AccountEditModal = ({ show, onHide, onSubmit, formData, setFormData, accounts = [], editingAccountId }) => {
  const [isSubAccount, setIsSubAccount] = useState(false);

  useEffect(() => {
    if (show) {
      setIsSubAccount(!!formData.parentId);
    }
  }, [show, formData.parentId]);

  // Recursively gather all child IDs of the currently editing account to prevent circular references
  const getChildrenIds = (id) => {
    if (!id) return [];
    const childIds = [];
    const children = accounts.filter(a => a.parentId && a.parentId.toString() === id.toString());
    children.forEach(child => {
      childIds.push(child._id);
      childIds.push(...getChildrenIds(child._id));
    });
    return childIds;
  };

  const invalidParentIds = editingAccountId ? [editingAccountId, ...getChildrenIds(editingAccountId)] : [];
  const validParents = accounts.filter(a => !invalidParentIds.includes(a._id));

  const parentOptions = validParents.map(acc => ({
    value: acc._id,
    label: acc.accountNumber ? `${acc.accountNumber} · ${acc.name}` : acc.name
  }));

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsSubAccount(checked);
    if (!checked) {
      setFormData({ ...formData, parentId: '' });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="h5 font-weight-bold">Edit Account</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small font-weight-bold text-muted text-uppercase">Account Number (Optional)</Form.Label>
            <Form.Control 
              type="text" size="sm"
              value={formData.accountNumber} 
              onChange={e => setFormData({...formData, accountNumber: e.target.value})} 
              placeholder="e.g. 1001"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small font-weight-bold text-muted text-uppercase">Name</Form.Label>
            <Form.Control 
              type="text" size="sm"
              value={formData.name} 
              onChange={e => setFormData({...formData, name: e.target.value})} 
              required 
            />
          </Form.Group>

          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Check 
              type="checkbox"
              id="isSubAccountEdit"
              label="Is sub-account"
              checked={isSubAccount}
              onChange={handleCheckboxChange}
              className="mr-2"
            />
          </Form.Group>

          {isSubAccount && (
            <Form.Group className="mb-3">
              <Form.Label className="small font-weight-bold text-muted text-uppercase">Parent Account</Form.Label>
              <SearchableSelect 
                placeholder="Search and select parent..."
                value={formData.parentId}
                onChange={parentId => {
                  const parentAcc = accounts.find(a => a._id === parentId);
                  if (parentAcc) {
                    setFormData({
                      ...formData,
                      parentId,
                      type: parentAcc.type
                    });
                  } else {
                    setFormData({
                      ...formData,
                      parentId: ''
                    });
                  }
                }}
                options={parentOptions}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="small font-weight-bold text-muted text-uppercase">Type</Form.Label>
            <Form.Control 
              as="select" size="sm"
              value={formData.type} 
              onChange={e => setFormData({...formData, type: e.target.value})} 
              required
              disabled={isSubAccount && !!formData.parentId}
            >
              <optgroup label="Assets">
                <option value="Bank">Bank</option>
                <option value="Accounts receivable (A/R)">Accounts receivable (A/R)</option>
                <option value="Other Current Assets">Other Current Assets</option>
                <option value="Fixed Assets">Fixed Assets</option>
                <option value="Other Assets">Other Assets</option>
              </optgroup>
              <optgroup label="Liabilities">
                <option value="Credit Card">Credit Card</option>
                <option value="Accounts payable (A/P)">Accounts payable (A/P)</option>
                <option value="Other Current Liabilities">Other Current Liabilities</option>
                <option value="Long Term Liabilities">Long Term Liabilities</option>
              </optgroup>
              <optgroup label="Equity">
                <option value="Equity">Equity</option>
              </optgroup>
              <optgroup label="Income">
                <option value="Income">Income</option>
                <option value="Other Income">Other Income</option>
              </optgroup>
              <optgroup label="Expenses">
                <option value="Cost of Goods Sold">Cost of Goods Sold</option>
                <option value="Expenses">Expenses</option>
                <option value="Expense" style={{ display: 'none' }}>Expenses</option>
                <option value="Other Expense">Other Expense</option>
              </optgroup>
            </Form.Control>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" size="sm" onClick={onHide}>Cancel</Button>
          <Button variant="primary" size="sm" type="submit" className="font-weight-bold">Save Changes</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AccountEditModal;
